/* ltt-options — validates the coded option groups before add-to-cart.
 *
 * Replaces the Globo app's client-side validation (D-18). Native `required` on a
 * radio covers the single-choice groups, but checkbox groups with "pick exactly
 * N" (bridal-box asks for exactly 4 fragrances out of 8) have no HTML
 * equivalent, so that part is enforced here.
 *
 * Blocks submit rather than trusting the server: Shopify's /cart/add accepts any
 * line item properties without validating them, which is exactly how the bypass
 * happened in the first place.
 */
(() => {
  /* Set by ltt-product-options.liquid from the theme locales, so the messages
     follow the page's language. The fallbacks are English because English is
     the store's primary locale — an Arabic fallback used to be the default
     here, which meant a shopper on the English site was told off in Arabic if
     the strings ever failed to load. */
  const t = (window.lttOptionStrings = window.lttOptionStrings || {});
  const msgRequired = t.required || 'Please choose %s';
  const msgExactly = t.exactly || 'Pick exactly %d';

  /* From the locales, not hardcoded. It was '، ' — an Arabic comma, which the
     English storefront then printed on its own order lines:
     "Gucci، Midnight، Golden Dust". Same reasoning as the messages above: the
     code fallback is the English one, because English is the primary locale. */
  const SEPARATOR = t.separator || ', ';

  /* Multi-select groups post one joined string from a hidden field rather than
     `properties[x][]`, because Shopify prints array properties with no
     separator between the values on the cart line and the order. See the
     comment in ltt-product-options.liquid. */
  const syncJoined = (group) => {
    const joined = group.querySelector('[data-ltt-option-joined]');
    if (!joined) return;
    joined.value = [...group.querySelectorAll('input[type="checkbox"]')]
      .filter((b) => b.checked)
      .map((b) => b.value)
      .join(SEPARATOR);
  };

  const groupState = (group) => {
    const inputs = [...group.querySelectorAll('input, select')].filter(
      (i) => i.type !== 'hidden'
    );
    const chosen = inputs.filter((i) =>
      i.tagName === 'SELECT' ? i.value !== '' : i.checked
    ).length;
    const min = parseInt(group.dataset.min, 10);
    const max = parseInt(group.dataset.max, 10);
    return { chosen, min: Number.isNaN(min) ? null : min, max: Number.isNaN(max) ? null : max };
  };

  const validateGroup = (group) => {
    const { chosen, min, max } = groupState(group);
    const required = group.dataset.required === 'true';
    const error = group.querySelector('[data-ltt-option-error]');
    let message = '';

    // "exactly N" is expressed as min === max in the plan JSON.
    if (min !== null && max !== null && min === max) {
      if (chosen !== max) message = msgExactly.replace('%d', max);
    } else if (max !== null && chosen > max) {
      message = msgExactly.replace('%d', max);
    } else if (min !== null && chosen < min) {
      message = msgExactly.replace('%d', min);
    } else if (required && chosen === 0) {
      // %s rather than appending the name, so Arabic and English can each put
      // the group name where their own grammar wants it.
      message = msgRequired.replace('%s', group.dataset.name || '').trim();
    }

    if (error) {
      error.textContent = message;
      error.hidden = message === '';
    }
    group.classList.toggle('ltt-options__group--invalid', message !== '');
    return message === '';
  };

  const wire = (container) => {
    if (container.dataset.lttWired === 'true') return;
    const groups = [...container.querySelectorAll('[data-ltt-option-group]')];
    if (!groups.length) return;
    container.dataset.lttWired = 'true';

    // Re-validate on change so the shopper sees the count settle as they click,
    // but only after the group has been touched once — no red text on arrival.
    groups.forEach((group) => {
      group.addEventListener('change', () => {
        syncJoined(group);
        if (group.dataset.touched === 'true') validateGroup(group);
        group.dataset.touched = 'true';

        // Enforce the ceiling by disabling the unchecked boxes once it is hit,
        // which is friendlier than letting someone pick 5 and then scolding them.
        const { chosen, max } = groupState(group);
        if (max !== null) {
          group.querySelectorAll('input[type="checkbox"]').forEach((box) => {
            box.disabled = !box.checked && chosen >= max;
          });
        }
      });
    });

    const form =
      container.closest('form[action*="/cart/add"]') ||
      document.getElementById(groups[0].querySelector('input, select')?.getAttribute('form'));
    if (!form) return;

    form.addEventListener(
      'submit',
      (event) => {
        // Re-derive the joined value here too: a checkbox set programmatically
        // fires no `change`, and posting a stale hidden field would put the
        // wrong fragrances on a real order.
        groups.forEach((g) => {
          syncJoined(g);
          g.dataset.touched = 'true';
        });
        const bad = groups.filter((g) => !validateGroup(g));
        if (!bad.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        bad[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        bad[0].querySelector('input, select')?.focus();
      },
      // Capture, so this runs BEFORE Dawn's product-form.js submit handler and
      // can stop the fetch from ever leaving.
      true
    );
  };

  const init = () => document.querySelectorAll('[data-ltt-options]').forEach(wire);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* 🔴 Quick view inserts its markup AFTER this file has run, and when the file
     is already on the page its loader skips it, so neither path re-runs init.
     Left at the one-shot version the popup drew the option groups and validated
     none of them — the same silent bypass that let an order through with one
     fragrance instead of four, just on a different surface.

     An observer rather than a hook the dialog calls: every future surface that
     injects a product form is covered without having to remember this file
     exists. wire() is idempotent, so re-running costs nothing. */
  if (typeof MutationObserver === 'function') {
    /* setTimeout, not requestAnimationFrame: rAF is part of the rendering
       cycle and does not run in a background or hidden tab, so a dialog opened
       there would have stayed unvalidated. Measured — the popup's groups were
       still unwired until the tab was painted. */
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        init();
      }, 0);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
